// Static stall data store for development and testing
// This will be replaced by real API data later

import { getProductById } from './productStore';

export interface StoreLocation {
  id: string;
  name: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  phone?: string;
  hours?: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  distance?: number; // Distance from user location or city center
  transportation?: {
    metro?: string;
    bus?: string;
    parking?: string;
    additional?: string;
  };
}

export interface Stall {
  id: string;
  name: string;
  description: string;
  logo: string;
  location: string; // Main location for backward compatibility
  locations?: StoreLocation[]; // Multiple store locations
  coordinates?: {
    lat: number;
    lng: number;
  }; // Main coordinates for backward compatibility
  rating: number;
  reviewCount: number;
  productIds: number[]; // References to product IDs in ProductStore
  followerCount: number;
  isVerified: boolean;
  category: string;
  joinedDate: string;
  viewCount: number;
  freeShippingThreshold?: number; // Free shipping above this amount in euros
  // White-label landing page configuration
  landingPage?: {
    isEnabled: boolean;
    slug: string; // URL-friendly name for the landing page
    heroSection: {
      title: string;
      subtitle: string;
      backgroundImage?: string;
      ctaButton: {
        text: string;
        linkToFullStall: boolean;
      };
    };
    featuredProductIds: number[]; // Seller-selected featured products
    customization: {
      primaryColor?: string;
      showMostViewed: boolean;
      showOnSale: boolean;
    };
  };
}

// Static stall listings for development
// Product IDs are referenced from ProductStore.ts
export const stallListings: Record<string, Stall> = {
  "1": {
    id: "1",
    name: "Vintage Style Co.",
    description: "Curated collection of authentic vintage clothing and accessories from the 20th century.",
    logo: "/logos/1_Vintage_Style_Co._logo.png",
    location: "Lisbon, Portugal",
    coordinates: {
      lat: 38.7223,
      lng: -9.1393
    },
    locations: [
      {
        id: "main-store",
        name: "Chiado Main Store",
        address: "Rua Garrett 120, 1200-203 Lisbon, Portugal",
        coordinates: { lat: 38.7087, lng: -9.1403 },
        phone: "+351 21 342 1567",
        distance: 0.8,
        hours: {
          monday: "10:00 - 19:00",
          tuesday: "10:00 - 19:00",
          wednesday: "10:00 - 19:00",
          thursday: "10:00 - 20:00",
          friday: "10:00 - 19:00",
          saturday: "10:00 - 19:00",
          sunday: "11:00 - 18:00"
        },
        transportation: {
          metro: "Chiado station (3 min walk) - Green Line",
          bus: "Lines 758, 773 (1 min walk from stop)",
          parking: "Chiado car park (2 min walk) - €3/hour",
          additional: "Taxi rank available at Praça Luís de Camões (2 min walk)"
        }
      },
      {
        id: "baixa-branch",
        name: "Baixa Branch",
        address: "Rua Augusta 250, 1100-053 Lisbon, Portugal",
        coordinates: { lat: 38.7081, lng: -9.1377 },
        phone: "+351 21 342 8901",
        distance: 1.2,
        hours: {
          monday: "10:00 - 19:00",
          tuesday: "10:00 - 19:00",
          wednesday: "10:00 - 19:00",
          thursday: "10:00 - 20:00",
          friday: "10:00 - 19:00",
          saturday: "10:00 - 19:00",
          sunday: "11:00 - 18:00"
        },
        transportation: {
          metro: "Baixa-Chiado station (2 min walk) - Blue & Green Lines",
          bus: "Lines 711, 732, 736 (1 min walk from stop)",
          parking: "Praça do Comércio car park (3 min walk) - €2.50/hour",
          additional: "Tram 28 stops nearby (3 min walk) - Historic tram route"
        }
      },
      {
        id: "bairro-alto",
        name: "Bairro Alto Boutique",
        address: "Rua da Rosa 145, 1200-385 Lisbon, Portugal",
        coordinates: { lat: 38.7142, lng: -9.1456 },
        phone: "+351 21 343 2222",
        distance: 2.1,
        hours: {
          monday: "11:00 - 19:00",
          tuesday: "11:00 - 19:00",
          wednesday: "11:00 - 19:00",
          thursday: "11:00 - 20:00",
          friday: "11:00 - 20:00",
          saturday: "10:00 - 20:00",
          sunday: "12:00 - 18:00"
        },
        transportation: {
          metro: "Rato station (7 min walk) - Yellow Line",
          bus: "Lines 702, 773, 790 (2 min walk from stop)",
          parking: "Parque Mayer car park (4 min walk) - €2/hour",
          additional: "Elevador da Glória funicular (5 min walk) - Historic transport"
        }
      }
    ],
    rating: 4.3,
    reviewCount: 127,
    productIds: [1, 2, 7, 18, 27, 31, 35, 40, 42], // 9 products
    followerCount: 892,
    isVerified: true,
    category: "Clothing",
    joinedDate: "2023-01-15",
    viewCount: 15420,
    freeShippingThreshold: 75,
    landingPage: {
      isEnabled: true,
      slug: "vintage-style-co",
      heroSection: {
        title: "Discover Timeless Vintage Fashion",
        subtitle: "Authentic pieces from the golden eras of style",
        ctaButton: {
          text: "Shop Vintage Collection",
          linkToFullStall: true
        }
      },
      featuredProductIds: [1, 2, 7], // Featured products
      customization: {
        primaryColor: "#8B4513",
        showMostViewed: true,
        showOnSale: true
      }
    }
  },
  "2": {
    id: "2",
    name: "Lisboa Crafts",
    description: "Independent artisan collective selling handmade ceramics, textiles, and decor.",
    logo: "/logos/2_Lisboa_Crafts_logo.png",
    location: "Porto, Portugal",
    rating: 4.7,
    reviewCount: 89,
    productIds: [2, 4, 22, 23, 24, 34, 41], // 7 products
    followerCount: 1205,
    isVerified: true,
    category: "Art & Crafts",
    joinedDate: "2023-03-22",
    viewCount: 9876,
    freeShippingThreshold: 50,
  },
  "3": {
    id: "3",
    name: "Retro Finds",
    description: "Specializing in vintage electronics and collectibles from the 20th century.",
    logo: "/logos/3_Retro_Finds_logo.png",
    location: "Coimbra, Portugal",
    rating: 4.1,
    reviewCount: 156,
    productIds: [3, 6, 20, 21, 26, 33], // 6 products
    followerCount: 634,
    isVerified: false,
    category: "Electronics",
    joinedDate: "2023-05-10",
    viewCount: 5432,
  },
  "4": {
    id: "4",
    name: "Heritage Pieces",
    description: "Specializing in antique furniture and home decor items.",
    logo: "/logos/4_Heritage_Pieces_logo.png",
    location: "Braga, Portugal",
    rating: 4.5,
    reviewCount: 203,
    productIds: [4, 8, 19, 25, 28, 32, 36], // 7 products
    followerCount: 1456,
    isVerified: true,
    category: "Home & Garden",
    joinedDate: "2022-11-08",
    viewCount: 12345,
  },
  "5": {
    id: "5",
    name: "Music Memorabilia",
    description: "Rare vinyl records and music memorabilia from various eras.",
    logo: "/logos/5_Music_Memorabilia_logo.png",
    location: "Faro, Portugal",
    rating: 4.8,
    reviewCount: 67,
    productIds: [5, 10, 11, 37], // 4 products
    followerCount: 723,
    isVerified: true,
    category: "Music",
    joinedDate: "2023-07-14",
    viewCount: 8765,
  },
  "6": {
    id: "6",
    name: "Pixel Palace",
    description: "Vintage gaming consoles and classic video games.",
    logo: "/logos/6_Pixel_Palace_logo.png",
    location: "Aveiro, Portugal",
    rating: 4.2,
    reviewCount: 94,
    productIds: [6, 12, 13, 29, 38], // 5 products
    followerCount: 567,
    isVerified: false,
    category: "Gaming",
    joinedDate: "2023-09-03",
    viewCount: 4321,
  },
  "7": {
    id: "7",
    name: "Antique Lighting",
    description: "Specializing in vintage and antique lighting fixtures.",
    logo: "/logos/7_Antique_Lighting_logo.png",
    location: "Évora, Portugal",
    rating: 4.6,
    reviewCount: 78,
    productIds: [8, 14, 15], // 3 products
    followerCount: 892,
    isVerified: true,
    category: "Home & Garden",
    joinedDate: "2023-04-18",
    viewCount: 6543,
  },
  "8": {
    id: "8",
    name: "Comic Vault",
    description: "Rare comic books and graphic novels from various eras.",
    logo: "/logos/8_Comic_Vault_logo.png",
    location: "Viseu, Portugal",
    rating: 4.7,
    reviewCount: 112,
    productIds: [9, 16, 17, 30, 39], // 5 products
    followerCount: 1234,
    isVerified: true,
    category: "Books",
    joinedDate: "2023-02-28",
    viewCount: 9876,
  },
};

// Helper function to get a stall by ID
export function getStallById(id: string): Stall | undefined {
  return stallListings[id];
}

// Helper function to get all stalls
export function getAllStalls(): Stall[] {
  return Object.values(stallListings);
}

// Helper function to search stalls by name, description, or category
export function searchStalls(query: string): Stall[] {
  const lowercaseQuery = query.toLowerCase();
  return Object.values(stallListings).filter(stall =>
    stall.name.toLowerCase().includes(lowercaseQuery) ||
    stall.description.toLowerCase().includes(lowercaseQuery) ||
    stall.category.toLowerCase().includes(lowercaseQuery)
  );
}

// Helper function to get stalls by category
export function getStallsByCategory(category: string): Stall[] {
  return Object.values(stallListings).filter(stall =>
    stall.category.toLowerCase() === category.toLowerCase()
  );
}

// Helper function to get most recent stalls
export function getMostRecentStalls(limit?: number): Stall[] {
  const sorted = Object.values(stallListings).sort((a, b) =>
    new Date(b.joinedDate).getTime() - new Date(a.joinedDate).getTime()
  );
  return limit ? sorted.slice(0, limit) : sorted;
}

// Helper function to get most viewed stalls
export function getMostViewedStalls(limit?: number): Stall[] {
  const sorted = Object.values(stallListings).sort((a, b) =>
    b.viewCount - a.viewCount
  );
  return limit ? sorted.slice(0, limit) : sorted;
}

// Helper function to get top rated stalls
export function getTopRatedStalls(limit?: number): Stall[] {
  const sorted = Object.values(stallListings).sort((a, b) =>
    b.rating - a.rating
  );
  return limit ? sorted.slice(0, limit) : sorted;
}

// Helper function to get most followed stalls
export function getMostFollowedStalls(limit?: number): Stall[] {
  const sorted = Object.values(stallListings).sort((a, b) =>
    b.followerCount - a.followerCount
  );
  return limit ? sorted.slice(0, limit) : sorted;
}

// Helper function to get product count for a stall
export function getStallProductCount(stallId: string): number {
  const stall = stallListings[stallId];
  return stall ? stall.productIds.length : 0;
}

// Helper function to get products for a specific stall
export function getStallProducts(stallId: string) {
  const stall = stallListings[stallId];

  if (!stall) return [];

  return stall.productIds
    .map(productId => getProductById(productId))
    .filter(product => product !== undefined);
}
